/**
 * @file AdminDashboard.jsx
 * @description Admin-only real-time analytics dashboard at /admin.
 *
 * ── ARCHITECTURE ─────────────────────────────────────────────────────
 * Data sources — all real, live from MongoDB via /api/analytics/*
 *   GET /api/analytics/overview     → KPI cards (8 tiles)
 *   GET /api/analytics/live         → Online counter + active sessions + activity feed
 *   GET /api/analytics/timeseries   → Line chart data (30 days)
 *   GET /api/analytics/hourly       → Bar chart data (24 hours today)
 *   GET /api/analytics/distribution → Pie charts (device + country)
 *   GET /api/analytics/top          → Top pages + top tools lists
 *
 * ── REFRESH CADENCE ─────────────────────────────────────────────────
 * overview + live     → every 10 s (near real-time)
 * timeseries + hourly → every 60 s
 * distribution + top  → every 60 s
 *
 * ── AUTH ────────────────────────────────────────────────────────────
 * All /api/analytics/* admin endpoints hard-guard on `user.is_admin`.
 * This page mirrors that check client-side to render a friendly 403 UI.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, Users, Eye, Clock, TrendingUp, TrendingDown, Wifi,
  ShieldAlert, Sparkles, RefreshCw, Globe, Smartphone, Monitor,
  Tablet, ExternalLink, ArrowUpRight, ArrowDownRight, Crown,
  Mail, Download, Trash2, ChevronDown, DollarSign, Award,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const CORAL = "#F97316";
const NAVY = "#0B1220";
const CHART_COLORS = ["#F97316", "#2E78DC", "#3CB098", "#E85252", "#8A93A6", "#FFB020", "#7A5AF8", "#22C7BB"];

// ────────────────────────────────────────────────────────────────────
// Small utilities
// ────────────────────────────────────────────────────────────────────
const fmt = (n) => (n === null || n === undefined || Number.isNaN(n) ? "—" : Number(n).toLocaleString());
const fmtDuration = (s) => {
  if (!s) return "0s";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
};
const fmtRelative = (iso) => {
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
};
const deviceIcon = (d) => {
  const key = (d || "").toLowerCase();
  if (key.includes("mobile")) return <Smartphone size={12} />;
  if (key.includes("tablet")) return <Tablet size={12} />;
  return <Monitor size={12} />;
};

// ────────────────────────────────────────────────────────────────────
// KPI card
// ────────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, delta, icon: Icon, tone = "coral", testId }) {
  const isUp = typeof delta === "number" && delta >= 0;
  return (
    <div className="curio-card !rounded-2xl p-4 flex flex-col justify-between h-full min-h-[110px]" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#5B6478]">{label}</span>
        {Icon && (
          <Icon
            size={16}
            className={tone === "coral" ? "text-[var(--curio-coral)]" : "text-[#0B1220]"}
          />
        )}
      </div>
      <div className="mt-2 flex items-end justify-between gap-3">
        <div className="text-2xl font-bold text-[#0B1220] font-mono leading-none">{value}</div>
        {typeof delta === "number" && (
          <span
            className={`text-[10px] font-bold flex items-center gap-0.5 ${isUp ? "text-emerald-600" : "text-rose-600"}`}
          >
            {isUp ? <ArrowUpRight size={11}/> : <ArrowDownRight size={11}/>}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
      {sub && <div className="mt-1 text-[10px] text-[#8A93A6]">{sub}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Loading skeleton
// ────────────────────────────────────────────────────────────────────
const Skel = ({ h = "h-24", w = "w-full" }) => (
  <div className={`${w} ${h} rounded-2xl bg-[#F0F1F5] animate-pulse`}/>
);

// ────────────────────────────────────────────────────────────────────
// Premium Members panel — every user with paid/founding access
//   Founders (holding founding_number 1..250) sort to the top.
//   Source column tells admins whether they paid via Stripe, were manually
//   granted access, or are a Founding Seed (owner-seeded seat #1).
// ────────────────────────────────────────────────────────────────────
function PremiumMembersPanel() {
  const [data, setData] = React.useState(null);
  const [q, setQ] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/admin/premium-members", { params: { limit: 500 } });
      setData(r.data);
    } catch (_) { /* non-fatal */ }
  }, []);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = React.useMemo(() => {
    if (!data?.items) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.items;
    return data.items.filter((m) =>
      (m.email || "").toLowerCase().includes(needle) ||
      (m.name  || "").toLowerCase().includes(needle) ||
      (m.tier_tags || []).some((t) => t.toLowerCase().includes(needle))
    );
  }, [data, q]);

  const sourcePill = (source, label) => {
    const cls =
      source === "stripe"        ? "bg-emerald-100 text-emerald-700" :
      source === "founding_seed" ? "bg-orange-100 text-orange-700"   :
                                   "bg-blue-100 text-blue-700";
    return <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
  };

  return (
    <div className="curio-card !rounded-2xl p-4 mb-4" data-testid="premium-members-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase flex items-center gap-1.5">
            <Crown size={12} className="text-[#F97316]"/> Premium Members
          </div>
          <div className="text-sm font-semibold text-[#0B1220]">
            {data ? (
              <>
                {fmt(data.total)} premium · {fmt(data.founders)} founder{data.founders === 1 ? "" : "s"} · {fmt(data.by_source?.stripe || 0)} Stripe · {fmt((data.by_source?.manual || 0) + (data.by_source?.founding_seed || 0))} manual
              </>
            ) : "Loading…"}
          </div>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email, name, or tier…"
          className="text-[11.5px] w-56 h-8 rounded-md bg-white border border-[#E7EAF0] px-2.5 focus:outline-none focus:border-[#0B1220]/40"
          data-testid="premium-members-search"
        />
      </div>

      {!data ? (
        <Skel h="h-32"/>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-[#8A93A6] py-6 text-center" data-testid="premium-members-empty">
          {q ? `No premium members match "${q}".` : "No premium members yet — first paid founder will show up here."}
        </div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto pr-1">
          <table className="w-full text-[12px]">
            <thead className="text-left text-[10px] uppercase tracking-wide text-[#8A93A6] sticky top-0 bg-white z-10">
              <tr>
                <th className="pb-2 pr-2 font-semibold">Member</th>
                <th className="pb-2 pr-2 font-semibold">Tiers</th>
                <th className="pb-2 pr-2 font-semibold">Source</th>
                <th className="pb-2 font-semibold text-right">Joined</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => {
                const key = m.user_id || m.email;
                return (
                  <tr key={key} className="border-t border-[#F0F1F5] hover:bg-[#FAFBFC]" data-testid={`premium-row-${(m.email || key || "").split("@")[0]}`}>
                    <td className="py-2 pr-2 align-top">
                      <div className="flex items-center gap-2">
                        {m.founding_number ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-[#0B1220] text-white px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap"
                            title={`Founding Member #${m.founding_number}`}
                            data-testid={`premium-founding-${m.founding_number}`}
                          >
                            <Award size={9} className="text-[#F97316]" strokeWidth={2.5}/>
                            #{m.founding_number}
                          </span>
                        ) : null}
                        <div className="min-w-0">
                          <div className="text-[12.5px] font-semibold text-[#0B1220] truncate max-w-[240px]">
                            {m.name || "—"}
                            {m.is_admin && (
                              <span className="ml-1.5 text-[9px] uppercase tracking-wider font-bold text-rose-600 align-middle">Admin</span>
                            )}
                          </div>
                          <div className="text-[10.5px] text-[#8A93A6] font-mono truncate max-w-[240px]">{m.email || m.user_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-2 align-top">
                      <div className="flex flex-wrap gap-1 max-w-[240px]">
                        {m.tier_tags.map((t) => (
                          <span key={t} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#F7F8FA] border border-[#E7EAF0] text-[#3D465C] whitespace-nowrap">
                            {t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 pr-2 align-top">
                      {sourcePill(m.source, m.source_label)}
                      {m.hidden_from_public && (
                        <div className="text-[9.5px] text-[#8A93A6] mt-1">hidden from public wall</div>
                      )}
                    </td>
                    <td className="py-2 text-right text-[10.5px] text-[#8A93A6] align-top whitespace-nowrap">
                      {m.created_at ? fmtRelative(m.created_at) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Founding Purchases panel — today's revenue + latest N completed founders
// ────────────────────────────────────────────────────────────────────
function FoundingPurchasesPanel() {
  const [data, setData] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/admin/payments/founding-analytics", { params: { limit: 5 } });
      setData(r.data);
    } catch (_) { /* non-fatal */ }
  }, []);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    // Auto-refresh every 30s — real-money surface, keep it snappy.
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const fmtMoney = (cents, ccy = "USD") => {
    const amt = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency", currency: ccy || "USD",
        maximumFractionDigits: 0,
      }).format(amt);
    } catch (_) { return `$${amt.toFixed(0)}`; }
  };

  return (
    <div className="curio-card !rounded-2xl p-4 mb-4" data-testid="founding-purchases-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase flex items-center gap-1.5">
            <Award size={12} className="text-[#F97316]"/> Founding Purchases · Live
          </div>
          <div className="text-sm font-semibold text-[#0B1220]">
            {data ? (
              <>
                {fmt(data.today.count)} today · {fmtMoney(data.today.revenue_cents)} today · {fmt(data.all_time.count)} all-time · {fmtMoney(data.all_time.revenue_cents)} all-time
              </>
            ) : "Loading…"}
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="wf-pill bg-white border border-[#E7EAF0] text-[#0B1220] flex items-center gap-1.5" data-testid="founding-seats-remaining">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#F97316]" aria-hidden="true"/>
            <strong className="font-mono tabular-nums">{data ? data.seats.claimed : "—"}</strong>
            <span className="text-[#8A93A6]">/ {data ? data.seats.cap : 250} claimed · {data ? data.seats.remaining : "—"} left</span>
          </span>
        </div>
      </div>

      {/* KPI mini-grid — today | all-time */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl bg-gradient-to-br from-[#F97316] to-[#EA580C] text-white p-3.5" data-testid="founding-today-revenue">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-white/70">Today's revenue</div>
          <div className="mt-1 text-2xl font-bold font-mono tabular-nums">{data ? fmtMoney(data.today.revenue_cents) : "…"}</div>
          <div className="mt-0.5 text-[11px] text-white/80">{data ? `${fmt(data.today.count)} new founder${data.today.count === 1 ? "" : "s"}` : "…"}</div>
        </div>
        <div className="rounded-xl bg-[#F7F8FA] border border-[#E7EAF0] p-3.5" data-testid="founding-alltime-revenue">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#5B6478]">All-time revenue</div>
          <div className="mt-1 text-2xl font-bold font-mono tabular-nums text-[#0B1220]">{data ? fmtMoney(data.all_time.revenue_cents) : "…"}</div>
          <div className="mt-0.5 text-[11px] text-[#8A93A6]">{data ? `${fmt(data.all_time.count)} total founder${data.all_time.count === 1 ? "" : "s"}` : "…"}</div>
        </div>
        <div className="rounded-xl bg-[#F7F8FA] border border-[#E7EAF0] p-3.5" data-testid="founding-avg-order">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#5B6478]">Avg order value</div>
          <div className="mt-1 text-2xl font-bold font-mono tabular-nums text-[#0B1220]">
            {data && data.all_time.count > 0
              ? fmtMoney(Math.round(data.all_time.revenue_cents / data.all_time.count))
              : "$0"}
          </div>
          <div className="mt-0.5 text-[11px] text-[#8A93A6]">$249 target</div>
        </div>
      </div>

      {/* Latest purchases */}
      <div>
        <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase mb-2">Latest 5 purchases</div>
        {!data ? (
          <Skel h="h-20"/>
        ) : data.latest_purchases.length === 0 ? (
          <div className="text-sm text-[#8A93A6] py-6 text-center" data-testid="founding-purchases-empty">
            No completed Founding purchases yet — the first live sale will land here in real time.
          </div>
        ) : (
          <table className="w-full text-[12px]">
            <thead className="text-left text-[10px] uppercase tracking-wide text-[#8A93A6]">
              <tr>
                <th className="pb-2 pr-2 font-semibold">#</th>
                <th className="pb-2 pr-2 font-semibold">Buyer</th>
                <th className="pb-2 pr-2 font-semibold">Amount</th>
                <th className="pb-2 font-semibold text-right">Completed</th>
              </tr>
            </thead>
            <tbody>
              {data.latest_purchases.map((p) => (
                <tr key={p.session_id} className="border-t border-[#F0F1F5] hover:bg-[#FAFBFC]" data-testid={`founding-purchase-${p.founding_number || 'x'}`}>
                  <td className="py-2 pr-2 font-mono font-bold text-[#F97316] tabular-nums">
                    #{p.founding_number ?? "—"}
                  </td>
                  <td className="py-2 pr-2 text-[#0B1220]">
                    <div className="font-semibold truncate max-w-[240px]">{p.name || "—"}</div>
                    <div className="text-[10.5px] text-[#8A93A6] font-mono truncate max-w-[240px]">{p.email || p.user_id}</div>
                  </td>
                  <td className="py-2 pr-2 font-mono tabular-nums text-[#0B1220]">
                    {fmtMoney(p.amount_cents, p.currency)}
                  </td>
                  <td className="py-2 text-right text-[10.5px] text-[#8A93A6]">
                    {p.completed_at ? fmtRelative(p.completed_at) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Reservations panel — collected emails from the Pricing page's Reserve form
// ────────────────────────────────────────────────────────────────────
function ReservationsPanel() {
  const [data, setData] = React.useState(null);
  const [filter, setFilter] = React.useState("all");
  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const r = await api.get("/admin/reservations", { params });
      setData(r.data);
    } catch (_) { /* non-fatal */ }
  }, [filter]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    // Keep it fresh but not chatty — 30s cadence.
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/reservations/${id}`, { status });
      toast.success(`Marked as ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusyId(null); }
  };

  const removeItem = async (id, email) => {
    if (!window.confirm(`Delete reservation for ${email}?`)) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/reservations/${id}`);
      toast.success("Reservation deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); }
  };

  const exportCsv = () => {
    if (!data?.items?.length) return;
    const rows = [
      ["email", "name", "status", "source", "created_at", "submission_count", "notes"],
      ...data.items.map((r) => [
        r.email || "",
        r.name || "",
        r.status || "",
        r.source || "",
        r.created_at || "",
        String(r.submission_count ?? 1),
        (r.notes || "").replace(/[\r\n,]/g, " "),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wikificial-reservations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const total = data?.total ?? 0;
  const pending = data?.by_status?.pending ?? 0;
  const contacted = data?.by_status?.contacted ?? 0;
  const claimed = data?.by_status?.claimed ?? 0;

  return (
    <div className="curio-card !rounded-2xl p-4 mb-4" data-testid="reservations-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase flex items-center gap-1.5">
            <Mail size={12}/> Founding Reservations
          </div>
          <div className="text-sm font-semibold text-[#0B1220]">
            {fmt(total)} email{total === 1 ? "" : "s"} collected · {fmt(pending)} pending · {fmt(contacted)} contacted · {fmt(claimed)} claimed
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-[11px] px-2.5 py-1.5 rounded-md bg-white border border-[#E7EAF0] focus:outline-none focus:border-[#0B1220]/40"
            data-testid="reservations-filter"
          >
            <option value="all">All statuses</option>
            <option value="pending">Pending</option>
            <option value="contacted">Contacted</option>
            <option value="claimed">Claimed</option>
            <option value="archived">Archived</option>
          </select>
          <button
            onClick={exportCsv}
            disabled={!data?.items?.length}
            className="inline-flex items-center gap-1.5 rounded-md bg-white border border-[#E7EAF0] px-2.5 py-1.5 text-[11px] font-semibold text-[#0B1220] hover:bg-[#F7F5F2] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="reservations-export"
          >
            <Download size={11}/> Export CSV
          </button>
        </div>
      </div>

      {!data ? (
        <Skel h="h-40"/>
      ) : data.items.length === 0 ? (
        <div className="text-sm text-[#8A93A6] py-8 text-center" data-testid="reservations-empty">
          No reservations yet{filter !== "all" ? ` in "${filter}"` : ""} — visitors who Reserve on the Pricing page will land here.
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto pr-1">
          <table className="w-full text-[12px]">
            <thead className="text-left text-[10px] uppercase tracking-wide text-[#8A93A6] sticky top-0 bg-white">
              <tr>
                <th className="pb-2 pr-2 font-semibold">Email</th>
                <th className="pb-2 pr-2 font-semibold">Name</th>
                <th className="pb-2 pr-2 font-semibold">Status</th>
                <th className="pb-2 pr-2 font-semibold">Source</th>
                <th className="pb-2 pr-2 font-semibold text-right">Reserved</th>
                <th className="pb-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => {
                const statusColor =
                  r.status === "claimed"   ? "bg-emerald-100 text-emerald-700" :
                  r.status === "contacted" ? "bg-blue-100 text-blue-700" :
                  r.status === "archived"  ? "bg-gray-100 text-gray-500" :
                                             "bg-orange-100 text-orange-700";
                return (
                  <tr key={r.id} className="border-t border-[#F0F1F5] hover:bg-[#FAFBFC]" data-testid={`reservation-row-${r.id.slice(0, 8)}`}>
                    <td className="py-2 pr-2 font-mono text-[11px] text-[#0B1220] truncate max-w-[240px]">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{r.email}</span>
                        {r.confirmation_email_status === "sent" && (
                          <span title="Confirmation email sent" className="text-emerald-500 text-[10px]" data-testid={`reservation-email-sent-${r.id.slice(0, 8)}`}>✓</span>
                        )}
                        {r.confirmation_email_status === "failed" && (
                          <span title={`Email failed: ${r.confirmation_email_error || "unknown"}`} className="text-rose-500 text-[10px]" data-testid={`reservation-email-failed-${r.id.slice(0, 8)}`}>⚠</span>
                        )}
                        {r.confirmation_email_status === "skipped" && (
                          <span title="Email skipped (mailer not configured)" className="text-amber-500 text-[10px]">⊘</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-[#3D465C] truncate max-w-[140px]">{r.name || "—"}</td>
                    <td className="py-2 pr-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${statusColor}`} data-testid={`reservation-status-${r.id.slice(0, 8)}`}>
                        {r.status}
                      </span>
                      {r.submission_count > 1 && (
                        <span className="ml-1.5 text-[10px] text-[#8A93A6]">×{r.submission_count}</span>
                      )}
                    </td>
                    <td className="py-2 pr-2 text-[#8A93A6] text-[11px]">{r.source || "—"}</td>
                    <td className="py-2 pr-2 text-right text-[10px] text-[#8A93A6]">{fmtRelative(r.created_at)}</td>
                    <td className="py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {r.status !== "contacted" && (
                          <button
                            onClick={() => updateStatus(r.id, "contacted")}
                            disabled={busyId === r.id}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-40"
                            title="Mark as contacted"
                            data-testid={`reservation-mark-contacted-${r.id.slice(0, 8)}`}
                          >Contacted</button>
                        )}
                        {r.status !== "claimed" && (
                          <button
                            onClick={() => updateStatus(r.id, "claimed")}
                            disabled={busyId === r.id}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
                            title="Mark as claimed"
                            data-testid={`reservation-mark-claimed-${r.id.slice(0, 8)}`}
                          >Claimed</button>
                        )}
                        <button
                          onClick={() => removeItem(r.id, r.email)}
                          disabled={busyId === r.id}
                          className="text-[10px] p-1 rounded text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                          title="Delete reservation"
                          data-testid={`reservation-delete-${r.id.slice(0, 8)}`}
                        ><Trash2 size={11}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { user } = useAuth();

  const [overview, setOverview] = useState(null);
  const [live, setLive] = useState(null);
  const [ts, setTs] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [dist, setDist] = useState(null);
  const [top, setTop] = useState(null);
  const [error, setError] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);

  // ── Fetchers ──────────────────────────────────────────────────────
  const loadFast = useCallback(async () => {
    try {
      const [ov, lv] = await Promise.all([
        api.get("/analytics/overview"),
        api.get("/analytics/live"),
      ]);
      setOverview(ov.data);
      setLive(lv.data);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setError("admin-required");
      } else {
        setError("network");
      }
    }
  }, []);

  const loadSlow = useCallback(async () => {
    try {
      const [t, h, d, tp] = await Promise.all([
        api.get("/analytics/timeseries", { params: { days: 30 } }),
        api.get("/analytics/hourly"),
        api.get("/analytics/distribution"),
        api.get("/analytics/top", { params: { limit: 10 } }),
      ]);
      setTs(t.data);
      setHourly(h.data);
      setDist(d.data);
      setTop(tp.data);
    } catch (e) {
      // The fast poll already showed the 403 / network UI; nothing else to do here.
    }
  }, []);

  // Initial load + polling
  useEffect(() => {
    if (!user?.is_admin) return;
    let mounted = true;
    (async () => {
      await Promise.all([loadFast(), loadSlow()]);
      if (mounted) setInitialLoad(false);
    })();
    const fast = setInterval(() => { if (mounted) loadFast(); }, 10_000);
    const slow = setInterval(() => { if (mounted) loadSlow(); }, 60_000);
    return () => { mounted = false; clearInterval(fast); clearInterval(slow); };
  }, [user?.is_admin, loadFast, loadSlow]);

  const manualRefresh = async () => {
    await Promise.all([loadFast(), loadSlow()]);
    toast.success("Dashboard refreshed");
  };

  // ── AUTH GUARDS ───────────────────────────────────────────────────
  if (!user) {
    return (
      <Layout hideRightPanel>
        <PageHero eyebrow="Admin" title={<>Analytics <span className="italic">dashboard</span></>} sub="Sign in as an admin to view live site metrics."/>
        <div className="curio-card p-6 text-sm text-[#3D465C]" data-testid="admin-signin-cta">
          Please <Link to="/login" className="text-[var(--curio-coral)] font-semibold">sign in</Link> first.
        </div>
      </Layout>
    );
  }
  if (!user.is_admin) {
    return (
      <Layout hideRightPanel>
        <PageHero eyebrow="Admin" title={<>Analytics <span className="italic">dashboard</span></>} sub="This dashboard is restricted to admins."/>
        <div className="curio-card p-6 flex items-center gap-3 text-sm text-[#3D465C]" data-testid="admin-forbidden">
          <ShieldAlert className="text-rose-500" size={18} />
          Your account (<span className="font-mono">{user.email}</span>) is not on the admin allowlist.
        </div>
      </Layout>
    );
  }

  // ── RENDER ────────────────────────────────────────────────────────
  const online = overview?.online_now ?? live?.online_now ?? 0;

  return (
    <Layout hideRightPanel>
      <PageHero
        eyebrow="Admin · Real-time"
        title={<>Analytics <span className="italic">dashboard</span></>}
        sub="Live visitor + engagement metrics across every domain this backend serves (preview + wikificial.com)."
        badge={<span className="flex items-center gap-2"><span className="relative inline-flex w-2 h-2"><span className="absolute inline-flex w-2 h-2 rounded-full bg-emerald-400 opacity-75 animate-ping"/><span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500"/></span>{online} online</span>}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="admin-dashboard-toolbar">
        <button
          onClick={manualRefresh}
          className="inline-flex items-center gap-2 rounded-md bg-white border border-[#E7EAF0] px-3 py-2 text-sm font-semibold text-[#0B1220] hover:bg-[#F7F5F2]"
          data-testid="admin-dashboard-refresh"
        >
          <RefreshCw size={14}/> Refresh now
        </button>
        <Link to="/admin/discoveries" className="inline-flex items-center gap-2 rounded-md bg-white border border-[#E7EAF0] px-3 py-2 text-sm font-semibold text-[#0B1220] hover:bg-[#F7F5F2]">
          Pending Discoveries →
        </Link>
        <div className="flex-1"/>
        <span className="text-[11px] text-[#8A93A6]" data-testid="admin-dashboard-cadence">
          Live counter refreshes every 10s · charts every 60s
        </span>
      </div>

      {error === "network" && (
        <div className="curio-card p-4 mb-4 text-sm text-rose-700 bg-rose-50 border-rose-200" data-testid="dashboard-error">
          Couldn't reach the analytics API. Retrying automatically…
        </div>
      )}

      {/* ── FOUNDING PURCHASES (LIVE REVENUE) ──────────────────── */}
      <FoundingPurchasesPanel/>

      {/* ── PREMIUM MEMBERS (founders + paid + manual grants) ── */}
      <PremiumMembersPanel/>

      {/* ── FOUNDING RESERVATIONS ────────────────────────────────── */}
      <ReservationsPanel/>

      {/* ── LIVE ONLINE WIDGET + ACTIVE SESSIONS ─────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div
          className="curio-card !rounded-2xl p-5 relative overflow-hidden lg:col-span-1"
          style={{ background: "linear-gradient(135deg, #0B1220 0%, #1E3A5F 60%, #2C3E5C 100%)" }}
          data-testid="live-online-widget"
        >
          <div className="text-[10px] tracking-[0.2em] font-bold text-white/60 uppercase">Currently online</div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className="text-5xl font-bold text-white font-mono leading-none" data-testid="online-count">
              {initialLoad && overview === null ? "…" : online}
            </div>
            <span className="flex items-center gap-1.5 text-emerald-300 text-xs font-semibold">
              <span className="relative inline-flex w-2 h-2">
                <span className="absolute inline-flex w-2 h-2 rounded-full bg-emerald-400 opacity-75 animate-ping"/>
                <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500"/>
              </span>
              LIVE
            </span>
          </div>
          <div className="mt-2 text-white/70 text-xs">
            {overview ? `${fmt(overview.today_visits)} visits today · ${fmt(overview.unique_visitors_today)} unique` : "…"}
          </div>
        </div>

        <div className="curio-card !rounded-2xl p-4 lg:col-span-2" data-testid="active-sessions-list">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase">Active sessions (last 5 min)</div>
            <Wifi size={14} className="text-emerald-500"/>
          </div>
          {!live ? (
            <div className="space-y-2">{[0,1,2].map(i => <Skel key={i} h="h-8"/>)}</div>
          ) : live.active_sessions.length === 0 ? (
            <div className="text-sm text-[#8A93A6] py-4 text-center" data-testid="no-active-sessions">
              No one is here right now — as visitors arrive they'll show up live.
            </div>
          ) : (
            <div className="max-h-56 overflow-y-auto pr-1">
              <table className="w-full text-[12px]">
                <thead className="text-left text-[10px] uppercase tracking-wide text-[#8A93A6]">
                  <tr><th className="pb-1 font-semibold">Session</th><th className="pb-1 font-semibold">Device</th><th className="pb-1 font-semibold">Country</th><th className="pb-1 font-semibold">Last page</th><th className="pb-1 font-semibold text-right">Active</th></tr>
                </thead>
                <tbody>
                  {live.active_sessions.map((s) => (
                    <tr key={s.session_id} className="border-t border-[#F0F1F5]" data-testid={`session-${s.session_id_short}`}>
                      <td className="py-1.5 font-mono text-[10px] text-[#3D465C]">{s.session_id_short}</td>
                      <td className="py-1.5"><span className="inline-flex items-center gap-1 text-[#3D465C]">{deviceIcon(s.device)}{s.device}</span></td>
                      <td className="py-1.5 text-[#3D465C]">{s.country || "—"}</td>
                      <td className="py-1.5 text-[#3D465C] truncate max-w-[240px]">{s.last_path}</td>
                      <td className="py-1.5 text-right text-[10px] text-[#8A93A6]">{fmtRelative(s.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── KPI CARDS ────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="kpi-grid">
        {!overview ? (
          [0,1,2,3,4,5,6,7].map(i => <Skel key={i} h="h-[110px]"/>)
        ) : (
          <>
            <KpiCard testId="kpi-total-visits" label="Total visits" value={fmt(overview.total_visits)} sub="All time · unique sessions" icon={Users}/>
            <KpiCard testId="kpi-today-visits" label="Today's visits" value={fmt(overview.today_visits)} delta={overview.today_vs_yesterday_pct} sub="vs. yesterday" icon={Activity}/>
            <KpiCard testId="kpi-unique-today" label="Unique visitors today" value={fmt(overview.unique_visitors_today)} icon={Sparkles}/>
            <KpiCard testId="kpi-total-pageviews" label="Total page views" value={fmt(overview.total_pageviews)} icon={Eye}/>
            <KpiCard testId="kpi-avg-duration" label="Avg session duration" value={fmtDuration(overview.avg_session_duration_sec)} sub="Last 7 days" icon={Clock}/>
            <KpiCard testId="kpi-bounce-rate" label="Bounce rate" value={`${overview.bounce_rate_pct.toFixed(1)}%`} sub="Last 7 days · 1-page sessions" icon={TrendingDown}/>
            <KpiCard testId="kpi-premium" label="Premium members" value={fmt(overview.premium_count)} sub="Pro + Plus + Founder" icon={Crown}/>
            <KpiCard testId="kpi-growth" label="Community growth" value={`${overview.community_growth_pct >= 0 ? "+" : ""}${overview.community_growth_pct.toFixed(1)}%`} delta={overview.community_growth_pct} sub="7d vs. prior 7d" icon={TrendingUp}/>
          </>
        )}
      </div>

      {/* ── LINE CHART: 30-day trends + BAR CHART: hourly today ─── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <div className="curio-card !rounded-2xl p-4 xl:col-span-2 min-h-[320px]" data-testid="chart-timeseries">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase">30-day trends</div>
              <div className="text-sm font-semibold text-[#0B1220]">Visits · Unique visitors · Page views</div>
            </div>
          </div>
          {!ts ? <Skel h="h-[260px]"/> : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={ts.series} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F5"/>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A93A6" }} tickFormatter={(d) => d.slice(5)}/>
                <YAxis tick={{ fontSize: 10, fill: "#8A93A6" }}/>
                <RTooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E7EAF0" }}/>
                <Legend wrapperStyle={{ fontSize: 11 }}/>
                <Line type="monotone" dataKey="visits" stroke={CORAL} strokeWidth={2} dot={false} name="Visits"/>
                <Line type="monotone" dataKey="unique_visitors" stroke={CHART_COLORS[1]} strokeWidth={2} dot={false} name="Unique"/>
                <Line type="monotone" dataKey="pageviews" stroke={CHART_COLORS[2]} strokeWidth={2} dot={false} name="Page views"/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="curio-card !rounded-2xl p-4 xl:col-span-1 min-h-[320px]" data-testid="chart-hourly">
          <div className="mb-2">
            <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase">Today · hourly</div>
            <div className="text-sm font-semibold text-[#0B1220]">Page views by hour (UTC)</div>
          </div>
          {!hourly ? <Skel h="h-[260px]"/> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={hourly.series} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F5"/>
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "#8A93A6" }} tickFormatter={(h) => `${String(h).padStart(2,"0")}h`} interval={2}/>
                <YAxis tick={{ fontSize: 10, fill: "#8A93A6" }}/>
                <RTooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E7EAF0" }}/>
                <Bar dataKey="pageviews" fill={CORAL} radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── PIE CHARTS: device + country ─────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="curio-card !rounded-2xl p-4 min-h-[280px]" data-testid="chart-devices">
          <div className="mb-2">
            <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase">Devices</div>
            <div className="text-sm font-semibold text-[#0B1220]">Traffic by device (last 30 days)</div>
          </div>
          {!dist ? <Skel h="h-[220px]"/> : dist.devices.length === 0 ? (
            <div className="text-sm text-[#8A93A6] py-10 text-center">No device data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={dist.devices} dataKey="value" nameKey="name" outerRadius={80} label={(e) => `${e.name} (${e.value})`}>
                  {dist.devices.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}
                </Pie>
                <RTooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E7EAF0" }}/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="curio-card !rounded-2xl p-4 min-h-[280px]" data-testid="chart-countries">
          <div className="mb-2">
            <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase">Geography</div>
            <div className="text-sm font-semibold text-[#0B1220]">Traffic by country (last 30 days)</div>
          </div>
          {!dist ? <Skel h="h-[220px]"/> : dist.countries.length === 0 ? (
            <div className="text-sm text-[#8A93A6] py-10 text-center">No country data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={dist.countries} dataKey="value" nameKey="name" outerRadius={80} label={(e) => `${e.name} (${e.value})`}>
                  {dist.countries.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}
                </Pie>
                <RTooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E7EAF0" }}/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── TOP LISTS + ACTIVITY FEED ────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="curio-card !rounded-2xl p-4" data-testid="top-pages">
          <div className="mb-2">
            <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase">Top pages</div>
            <div className="text-sm font-semibold text-[#0B1220]">Most viewed (last 30 days)</div>
          </div>
          {!top ? <Skel h="h-56"/> : top.pages.length === 0 ? (
            <div className="text-sm text-[#8A93A6] py-6 text-center">No page views yet.</div>
          ) : (
            <ol className="space-y-1.5">
              {top.pages.map((p, i) => (
                <li key={p.path} className="flex items-center justify-between text-[12px]" data-testid={`top-page-${i}`}>
                  <span className="truncate max-w-[70%] text-[#3D465C]"><span className="text-[#8A93A6] font-mono mr-2">{i+1}.</span>{p.path}</span>
                  <span className="font-mono font-bold text-[#0B1220]">{fmt(p.views)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="curio-card !rounded-2xl p-4" data-testid="top-tools">
          <div className="mb-2">
            <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase">Top AI tools</div>
            <div className="text-sm font-semibold text-[#0B1220]">Most viewed tool pages</div>
          </div>
          {!top ? <Skel h="h-56"/> : top.tools.length === 0 ? (
            <div className="text-sm text-[#8A93A6] py-6 text-center">No tool detail views yet.</div>
          ) : (
            <ol className="space-y-1.5">
              {top.tools.map((t, i) => (
                <li key={t.slug} className="flex items-center justify-between text-[12px]" data-testid={`top-tool-${i}`}>
                  <Link to={`/tool/${t.slug}`} className="truncate max-w-[70%] text-[#3D465C] hover:text-[var(--curio-coral)] flex items-center gap-1">
                    <span className="text-[#8A93A6] font-mono mr-1">{i+1}.</span>{t.slug}
                    <ExternalLink size={10}/>
                  </Link>
                  <span className="font-mono font-bold text-[#0B1220]">{fmt(t.views)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="curio-card !rounded-2xl p-4" data-testid="activity-feed">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#5B6478] uppercase">Live activity feed</div>
              <div className="text-sm font-semibold text-[#0B1220]">Recent page views</div>
            </div>
            <span className="relative inline-flex w-2 h-2">
              <span className="absolute inline-flex w-2 h-2 rounded-full bg-emerald-400 opacity-75 animate-ping"/>
              <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500"/>
            </span>
          </div>
          {!live ? <Skel h="h-56"/> : live.activity_feed.length === 0 ? (
            <div className="text-sm text-[#8A93A6] py-6 text-center">No recent activity in the last hour.</div>
          ) : (
            <ul className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
              {live.activity_feed.map((f, i) => (
                <li key={i} className="text-[11px] leading-tight flex items-start gap-2" data-testid={`activity-${i}`}>
                  <span className="mt-0.5 text-[#8A93A6]">{deviceIcon(f.device)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="text-[#3D465C] truncate block">{f.path}</span>
                    <span className="text-[#8A93A6]">{fmtRelative(f.ts)} · {f.country || "—"}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <p className="text-[11px] text-[#8A93A6] text-center pb-4">
        Data pipeline: `POST /api/analytics/track` fires on every route change. Sessions expire after 30 min of inactivity. Country resolved lazily via ip-api.com. IPs are SHA-256-hashed with a daily-rotating salt — no raw PII is stored.
      </p>
    </Layout>
  );
}
